import { computed, onBeforeMount, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

import useFeedDetails from '@/composables/useFeedDetails';
import {
  FILE_UPLOAD_DATA_SOURCE_TYPE,
  GOOGLE_SHEETS_DATA_SOURCE_TYPE,
  SNOWFLAKE_DATA_SOURCE_TYPE,
  SOURCE_STATUS_MAP,
  patchFeed,
} from '@/constants/data_sources';
import {
  BIG_CSV_FILE_LIMIT_500MB,
  TEMP_ALLOW_ADDING_TO_BIG_CSV,
} from '@/constants/feature_flags';
import { captureException } from '@/errors';
import {
  useDataSharesStore,
  useFeatureFlagStore,
  useFeedsStore,
  useFlashesStore,
  useSourcesStore,
} from '@/stores';
import { Feed, FeedFrequency } from '@/types/feeds';

/* Used to provide state and handlers for the data source settings modal,
 * including patching sync frequency, loading + saving state, etc. */

const loading = ref(true);
const feed = ref<Feed | null>(null);
const isSyncing = ref(false);
const currentFrequency = ref<FeedFrequency>(undefined);

export default function () {
  const route = useRoute();
  const flashesStore = useFlashesStore();
  const dataSharesStore = useDataSharesStore();
  const feedsStore = useFeedsStore();
  const sourcesStore = useSourcesStore();
  const { hasFeatureFlag } = useFeatureFlagStore();

  const feedId = computed(() => Number(route.params.id));
  const sourceId = computed(() => Number(route.params.source_id));
  const saving = ref(false);

  const isCSVFeed = computed(
    () => feed.value?.integration?.type === FILE_UPLOAD_DATA_SOURCE_TYPE,
  );
  const isGSheetFeed = computed(
    () => feed.value?.integration?.type === GOOGLE_SHEETS_DATA_SOURCE_TYPE,
  );
  const isSnowflakeFeed = computed(
    () => feed.value?.integration?.type === SNOWFLAKE_DATA_SOURCE_TYPE,
  );

  const source = computed(() => {
    if (sourceId.value) {
      return sourcesStore.getSourceById(sourceId.value) || null;
    }
    return sourcesStore.getSourcesByFeedId(feedId.value)[0] || null;
  });

  const sourceDisabled = computed(() => {
    if (!source.value?.status) return false;
    return (
      (SOURCE_STATUS_MAP.process as readonly string[]).includes(
        source.value.status,
      ) ||
      (SOURCE_STATUS_MAP.deleting as readonly string[]).includes(
        source.value.status,
      )
    );
  });

  const sourceDeleting = computed(() => {
    if (!source.value?.status) return false;
    return (SOURCE_STATUS_MAP.deleting as readonly string[]).includes(
      source.value.status,
    );
  });

  const hasBigCSVFileLimit = computed(() =>
    hasFeatureFlag(BIG_CSV_FILE_LIMIT_500MB),
  );
  const allowAddingToBigFiles = computed(() =>
    hasFeatureFlag(TEMP_ALLOW_ADDING_TO_BIG_CSV),
  );

  const allowAddData = computed(
    () =>
      isCSVFeed.value &&
      (!hasBigCSVFileLimit.value || allowAddingToBigFiles.value),
  );

  /* On initial component mount, populate the initial values for feed, frequency.
  If the feedId value from the route changes (feedId changes) then
  reset all of these values for the new feed */

  async function setValues() {
    if (feedId.value && feed.value?.id !== feedId.value) {
      loading.value = true;
      await feedsStore.readySync;
      feed.value = feedsStore.getFeedById(feedId.value) || null;
      isSyncing.value = !feed.value?.is_paused;
      currentFrequency.value = feed.value?.frequency as FeedFrequency;
    }

    loading.value = false;
  }

  onBeforeMount(setValues);
  watch([feedId], setValues);

  /* Update feed settings */
  async function handlePatchFeed() {
    saving.value = true;
    try {
      if (!feed.value) throw new Error('Feed not found');
      await patchFeed(feed.value.id, {
        is_paused: !isSyncing.value,
        frequency: currentFrequency.value ?? undefined,
      });
      flashesStore.addSuccessFlash({ message: 'Settings saved' });
      await Promise.all([
        dataSharesStore.refreshDataSharesStore(),
        feedsStore.refreshFeedsStore(),
        sourcesStore.refreshSourcesStore(),
      ]);
    } catch (err) {
      captureException(err);
      flashesStore.addErrorFlash({
        message: 'Could not save settings',
        description: 'If the error persists contact support@crossbeam.com',
      });
    } finally {
      saving.value = false;
    }
  }

  /* Reauthorize feed */
  const { reauthorize } = useFeedDetails({ feedId });
  async function handleReauthorize() {
    try {
      await reauthorize(feed.value);
    } catch (err) {
      captureException(err);
      flashesStore.addErrorFlash({
        message: 'Could not reauthorize feed',
        description: 'If the error persists contact support@crossbeam.com',
      });
    }
  }

  return {
    handlePatchFeed,
    handleReauthorize,
    loading,
    saving,
    isSyncing,
    feed,
    source,
    isCSVFeed,
    isGSheetFeed,
    isSnowflakeFeed,
    currentFrequency,
    allowAddData,
    sourceDisabled,
    sourceDeleting,
  };
}
